import clsx from 'clsx';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    clipboard,
    confirm,
    Description,
    DialogRoot,
    DialogTrigger,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
} from '@gitbook/spine-react';

import { EditVariableDialog } from './EditVariableDialog';

export interface VariablesFormProps {
    /**
     * The description to display in the empty state.
     */
    description: string;
    /**
     * The prefix to access the variable in the content.
     */
    prefix: string;
    /**
     * The variables to edit.
     */
    values: api.Variables;
    /**
     * Callback when the values change.
     */
    onChange: (values: api.Variables) => void;
    /**
     * Whether form fields will be read-only
     */
    readOnly?: boolean;
    /**
     * Class name to apply to the container.
     */
    className?: string;
}

/**
 * Form to edit variables.
 */
export function VariablesForm(props: VariablesFormProps): React.ReactElement {
    const { description, prefix, values, onChange, readOnly = false, className } = props;

    const entries = Object.entries(values);

    if (entries.length === 0) {
        return (
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.Variable />
                </EmptyStateIcon>
                <Heading>No variables defined</Heading>
                <Description>{description}</Description>
                {readOnly ? null : (
                    <EmptyStateActions>
                        <DialogRoot>
                            <DialogTrigger>
                                <Button
                                    kind="secondary"
                                    label="Create a variable"
                                    leadingIcon={Icon.Plus}
                                />
                            </DialogTrigger>

                            <EditVariableDialog
                                prefix={prefix}
                                onSubmit={(variable) => {
                                    onChange({ ...values, [variable.name]: variable.value });
                                }}
                            />
                        </DialogRoot>
                    </EmptyStateActions>
                )}
            </EmptyState>
        );
    }

    return (
        <div className={clsx('flex flex-col gap-4 p-4', className)}>
            <Description>{description}</Description>
            <div className="flex flex-1 flex-col gap-0.5">
                {entries.map(([name, value]) => {
                    const expression = `${prefix}${name}`;
                    return (
                        <DialogRoot key={name}>
                            <DialogTrigger>
                                <ListItem onPress={() => {}}>
                                    <ListItemIndicator>
                                        <Icon.ColumnTypeText className="size-4 text-muted" />
                                    </ListItemIndicator>
                                    <ListItemBody>
                                        <ListItemDescription>{name}</ListItemDescription>
                                        <ListItemDetail>{value}</ListItemDetail>
                                    </ListItemBody>
                                    <ListItemControls>
                                        <Button
                                            iconOnly
                                            kind="blank"
                                            label="Copy"
                                            tooltip={{
                                                label: 'Copy and then paste in your content',
                                                description: expression,
                                            }}
                                            leadingIcon={Icon.Copy}
                                            onPress={() => {
                                                clipboard.writeData({
                                                    'text/plain': `{{${expression}}}`,
                                                    'text/html': `<code class="expression">${expression}</code>`,
                                                });
                                            }}
                                        />
                                        <Button
                                            iconOnly
                                            label="Delete variable"
                                            leadingIcon={Icon.Trashcan}
                                            kind="blank"
                                            size="medium"
                                            onPress={() => {
                                                void confirm({
                                                    title: 'Delete variable',
                                                    description: (
                                                        <>
                                                            This variable will be deleted and all
                                                            existing uses will be replaced with an
                                                            empty value.
                                                        </>
                                                    ),
                                                    kind: 'danger',
                                                    confirmLabel: 'Delete',

                                                    onConfirm: () => {
                                                        const newValues = { ...values };
                                                        delete newValues[name];
                                                        onChange(newValues);
                                                    },
                                                });
                                            }}
                                        />
                                    </ListItemControls>
                                </ListItem>
                            </DialogTrigger>

                            <EditVariableDialog
                                prefix={prefix}
                                initialValue={{ name, value }}
                                onSubmit={(variable) => {
                                    onChange({ ...values, [variable.name]: variable.value });
                                }}
                            />
                        </DialogRoot>
                    );
                })}
                {readOnly ? null : (
                    <DialogRoot>
                        <DialogTrigger>
                            <ListItem>
                                <ListItemIndicator>
                                    <Icon.Plus className="size-4 text-muted" />
                                </ListItemIndicator>
                                <ListItemBody>
                                    <ListItemDescription>Add variable</ListItemDescription>
                                </ListItemBody>
                            </ListItem>
                        </DialogTrigger>

                        <EditVariableDialog
                            prefix={prefix}
                            onSubmit={(variable) => {
                                onChange({ ...values, [variable.name]: variable.value });
                            }}
                        />
                    </DialogRoot>
                )}
            </div>
        </div>
    );
}
